import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
    AlertTriangle, Search, Plus, Loader2, Trash2, Phone, Mail, X, Pencil, Check, FileText, Eye,
    ArrowLeft, ArrowRight, Star, Clock, Paperclip, Upload, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUTS = {
    a_traiter: { label: 'À traiter', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    en_cours: { label: 'En cours', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    resolu: { label: 'Résolu', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const STATUT_ORDER = { a_traiter: 0, en_cours: 1, resolu: 2 };
const sortProblemes = (list) => [...list].sort((a, b) => {
    const oa = STATUT_ORDER[a.statut] ?? 99;
    const ob = STATUT_ORDER[b.statut] ?? 99;
    if (oa !== ob) return oa - ob;
    return (b.created_at || '').localeCompare(a.created_at || '');
});

const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 Mo

function formatDate(d) { if (!d) return '-'; try { return format(new Date(d), 'dd/MM/yyyy HH:mm', { locale: fr }); } catch { return d; } }
function formatBytes(b) {
    if (!b && b !== 0) return '';
    if (b < 1024) return `${b} o`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} Ko`;
    return `${(b / 1024 / 1024).toFixed(1)} Mo`;
}

export default function ProblemesPage() {
    const navigate = useNavigate();
    const [problemes, setProblemes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterStatut, setFilterStatut] = useState('all');

    // Création (popup)
    const [showCreate, setShowCreate] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formClientNom, setFormClientNom] = useState('');
    const [formClientTel, setFormClientTel] = useState('');
    const [formClientEmail, setFormClientEmail] = useState('');
    const [formDescription, setFormDescription] = useState('');
    const [formFiles, setFormFiles] = useState([]);
    const fileInputRef = useRef(null);

    // Client search
    const [clientSearch, setClientSearch] = useState('');
    const [clientResults, setClientResults] = useState([]);
    const [showClientDropdown, setShowClientDropdown] = useState(false);

    // Coût (inline)
    const [editingCout, setEditingCout] = useState(null);
    const [editCoutValue, setEditCoutValue] = useState('');

    // Statut (popup remarque obligatoire)
    const [statutModal, setStatutModal] = useState(null); // { probleme, target }
    const [statutRemarque, setStatutRemarque] = useState('');
    const [statutSaving, setStatutSaving] = useState(false);

    // Historique
    const [historyProbleme, setHistoryProbleme] = useState(null);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Détail (description + fichiers)
    const [detailProbleme, setDetailProbleme] = useState(null);
    const [editingDescription, setEditingDescription] = useState(false);
    const [descriptionDraft, setDescriptionDraft] = useState('');
    const [savingDescription, setSavingDescription] = useState(false);
    const detailFileInputRef = useRef(null);
    const [uploadingFile, setUploadingFile] = useState(false);

    const fetchProblemes = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (search) params.append('search', search);
            if (filterStatut !== 'all') params.append('statut', filterStatut);
            const { data } = await axios.get(`${API}/api/problemes?${params}`, { withCredentials: true });
            const sorted = sortProblemes(data);
            setProblemes(sorted);
            return sorted;
        } catch { setProblemes([]); return []; }
        finally { setLoading(false); }
    }, [search, filterStatut]);

    useEffect(() => { fetchProblemes(); }, [fetchProblemes]);

    // Client search debounce
    useEffect(() => {
        if (clientSearch.length < 1) { setClientResults([]); return; }
        const timeout = setTimeout(async () => {
            try {
                const { data } = await axios.get(`${API}/api/problemes/search-clients?q=${encodeURIComponent(clientSearch)}`, { withCredentials: true });
                setClientResults(data);
                setShowClientDropdown(true);
            } catch { setClientResults([]); }
        }, 300);
        return () => clearTimeout(timeout);
    }, [clientSearch]);

    const selectClient = (client) => {
        setFormClientNom(client.client_nom);
        setFormClientTel(client.telephone || '');
        setFormClientEmail(client.email || '');
        setClientSearch(client.client_nom);
        setShowClientDropdown(false);
    };

    const resetForm = () => {
        setFormClientNom(''); setFormClientTel(''); setFormClientEmail('');
        setFormDescription(''); setFormFiles([]);
        setClientSearch(''); setClientResults([]); setShowClientDropdown(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleCreateFiles = (e) => {
        const incoming = Array.from(e.target.files || []);
        if (!incoming.length) return;
        if (formFiles.length + incoming.length > MAX_FILES) {
            toast.error(`Maximum ${MAX_FILES} fichiers par problème`);
            e.target.value = '';
            return;
        }
        for (const f of incoming) {
            if (f.size > MAX_FILE_SIZE) {
                toast.error(`Fichier "${f.name}" > 10 Mo`);
                e.target.value = '';
                return;
            }
        }
        setFormFiles(prev => [...prev, ...incoming]);
        e.target.value = '';
    };

    const handleCreate = async () => {
        if (!formClientNom.trim()) { toast.error('Veuillez sélectionner un client'); return; }
        if (!formDescription.trim()) { toast.error('Veuillez ajouter une description'); return; }
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('client_nom', formClientNom);
            fd.append('client_telephone', formClientTel);
            fd.append('client_email', formClientEmail);
            fd.append('description', formDescription);
            formFiles.forEach((f, i) => fd.append(`dossier_${i}`, f));
            await axios.post(`${API}/api/problemes`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success('Problème signalé');
            resetForm();
            setShowCreate(false);
            fetchProblemes();
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
        finally { setSaving(false); }
    };

    // Statut avec remarque obligatoire
    const openStatutModal = (probleme, target) => {
        setStatutModal({ probleme, target });
        setStatutRemarque('');
    };
    const submitStatut = async () => {
        if (!statutRemarque.trim()) return toast.error('Remarque obligatoire');
        setStatutSaving(true);
        try {
            await axios.patch(
                `${API}/api/problemes/${statutModal.probleme.id}/statut`,
                { statut: statutModal.target, remarque: statutRemarque.trim() },
                { withCredentials: true },
            );
            toast.success('Statut mis à jour');
            setStatutModal(null);
            fetchProblemes();
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
        finally { setStatutSaving(false); }
    };

    // Historique
    const openHistory = async (probleme) => {
        setHistoryProbleme(probleme);
        setHistoryLoading(true);
        try {
            const { data } = await axios.get(`${API}/api/problemes/${probleme.id}/historique`, { withCredentials: true });
            setHistoryData(data || []);
        } catch { setHistoryData([]); }
        finally { setHistoryLoading(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer ce problème ?')) return;
        try {
            await axios.delete(`${API}/api/problemes/${id}`, { withCredentials: true });
            toast.success('Problème supprimé');
            fetchProblemes();
        } catch { toast.error('Erreur'); }
    };

    const handleUpdateCout = async (id) => {
        try {
            await axios.patch(`${API}/api/problemes/${id}/cout`, { cout: editCoutValue }, { withCredentials: true });
            toast.success('Coût mis à jour');
            setEditingCout(null);
            fetchProblemes();
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    };

    // Détail : description
    const startEditDescription = () => {
        if (!detailProbleme) return;
        setDescriptionDraft(detailProbleme.description || '');
        setEditingDescription(true);
    };
    const cancelEditDescription = () => { setEditingDescription(false); setDescriptionDraft(''); };
    const refreshDetail = async (id) => {
        const list = await fetchProblemes();
        const fresh = (list || []).find(p => p.id === id);
        if (fresh) setDetailProbleme(fresh);
    };
    const saveDescription = async () => {
        if (!detailProbleme) return;
        const next = (descriptionDraft || '').trim();
        if (!next) { toast.error('Description obligatoire'); return; }
        if (next === (detailProbleme.description || '').trim()) { cancelEditDescription(); return; }
        setSavingDescription(true);
        try {
            await axios.patch(`${API}/api/problemes/${detailProbleme.id}/description`, { description: next }, { withCredentials: true });
            toast.success('Description mise à jour');
            cancelEditDescription();
            await refreshDetail(detailProbleme.id);
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
        finally { setSavingDescription(false); }
    };

    // Détail : fichiers
    const handleAddFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !detailProbleme) return;
        if (file.size > MAX_FILE_SIZE) { toast.error(`Fichier "${file.name}" > 10 Mo`); return; }
        setUploadingFile(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            await axios.post(`${API}/api/problemes/${detailProbleme.id}/files`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success('Fichier ajouté');
            await refreshDetail(detailProbleme.id);
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
        finally { setUploadingFile(false); }
    };

    const handleDeleteFile = async (filename) => {
        if (!detailProbleme) return;
        if (!window.confirm('Supprimer ce fichier ?')) return;
        try {
            await axios.delete(`${API}/api/problemes/${detailProbleme.id}/files/${filename}`, { withCredentials: true });
            toast.success('Fichier supprimé');
            await refreshDetail(detailProbleme.id);
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    };

    return (
        <div className="p-4 md:p-6 lg:p-8" data-testid="problemes-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
                        data-testid="problemes-back-btn"
                    >
                        <ArrowLeft size={16} className="mr-2" />
                        Retour
                    </Button>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="problemes-title">
                            Problèmes à régler
                        </h1>
                        <p className="mt-1 text-slate-500">Suivi des problèmes clients</p>
                    </div>
                </div>
                <Button
                    className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white gap-2"
                    onClick={() => { resetForm(); setShowCreate(true); }}
                    data-testid="probleme-new-btn"
                >
                    <Plus size={16} /> Signaler un problème
                </Button>
            </div>

            {/* Filtres */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
                <CardContent className="p-4 flex flex-col md:flex-row gap-3">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input placeholder="Rechercher (client, description)..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="probleme-search" />
                    </div>
                    <Select value={filterStatut} onValueChange={setFilterStatut}>
                        <SelectTrigger className="w-full md:w-[200px]" data-testid="probleme-filter-statut">
                            <SelectValue placeholder="Tous les statuts" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous les statuts</SelectItem>
                            {Object.entries(STATUTS).map(([v, info]) => (
                                <SelectItem key={v} value={v}>{info.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </CardContent>
            </Card>

            {/* Tableau */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                        <AlertTriangle size={20} className="text-amber-500" />
                        Liste des problèmes ({problemes.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" /></div>
                    ) : problemes.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">Aucun problème signalé</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table data-testid="problemes-table" className="min-w-[900px]">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-12"></TableHead>
                                        <TableHead className="w-10"></TableHead>
                                        <TableHead>Client</TableHead>
                                        <TableHead>Date création</TableHead>
                                        <TableHead>Description</TableHead>
                                        <TableHead>Coût</TableHead>
                                        <TableHead>Statut</TableHead>
                                        <TableHead>Modifié</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {problemes.map(p => (
                                        <TableRow key={p.id} className="hover:bg-slate-50" data-testid={`probleme-row-${p.id}`}>
                                            <TableCell className="w-12">
                                                <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center">
                                                    <AlertTriangle size={16} className="text-amber-500" />
                                                </div>
                                            </TableCell>
                                            <TableCell className="w-10">
                                                <button
                                                    onClick={() => openHistory(p)}
                                                    className="p-1 rounded-md hover:bg-blue-50 transition-colors"
                                                    title="Historique"
                                                    data-testid={`probleme-history-${p.id}`}
                                                >
                                                    <Star size={18} className="text-[#2e7cd6] fill-[#2e7cd6]" />
                                                </button>
                                            </TableCell>
                                            <TableCell>
                                                <p className="font-medium text-slate-900 text-sm">{p.client_nom || '-'}</p>
                                                <div className="flex flex-col gap-0.5 mt-1">
                                                    {p.client_telephone && (
                                                        <span className="flex items-center gap-1 text-[11px] text-slate-500"><Phone size={10} className="text-slate-400" />{p.client_telephone}</span>
                                                    )}
                                                    {p.client_email && (
                                                        <span className="flex items-center gap-1 text-[11px] text-slate-500"><Mail size={10} className="text-slate-400" />{p.client_email}</span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-sm text-slate-600">{formatDate(p.created_at)}</TableCell>
                                            <TableCell>
                                                <button
                                                    onClick={() => setDetailProbleme(p)}
                                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 hover:bg-[#2e7cd6] hover:text-white text-slate-700 text-xs font-medium transition-colors"
                                                    data-testid={`probleme-detail-btn-${p.id}`}
                                                >
                                                    <Eye size={12} />
                                                    Voir
                                                    {(p.dossiers || []).length > 0 && (
                                                        <span className="inline-flex items-center gap-0.5 opacity-70"><Paperclip size={10} />{p.dossiers.length}</span>
                                                    )}
                                                </button>
                                            </TableCell>
                                            <TableCell>
                                                {editingCout === p.id ? (
                                                    <div className="flex items-center gap-1">
                                                        <Input
                                                            value={editCoutValue}
                                                            onChange={e => setEditCoutValue(e.target.value)}
                                                            className="h-7 w-24 text-xs"
                                                            placeholder="Ex: 150 €"
                                                            autoFocus
                                                            data-testid={`probleme-cout-input-${p.id}`}
                                                            onKeyDown={e => { if (e.key === 'Enter') handleUpdateCout(p.id); if (e.key === 'Escape') setEditingCout(null); }}
                                                        />
                                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-emerald-500 hover:text-emerald-700" onClick={() => handleUpdateCout(p.id)} data-testid={`probleme-cout-save-${p.id}`}>
                                                            <Check size={14} />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600" onClick={() => setEditingCout(null)}>
                                                            <X size={14} />
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="text-sm font-medium text-slate-800">{p.cout || '-'}</span>
                                                        <button
                                                            onClick={() => { setEditingCout(p.id); setEditCoutValue(p.cout || ''); }}
                                                            className="text-slate-400 hover:text-[#2e7cd6] transition-colors"
                                                            data-testid={`probleme-cout-edit-${p.id}`}
                                                        >
                                                            <Pencil size={13} />
                                                        </button>
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Select value={p.statut} onValueChange={(v) => { if (v !== p.statut) openStatutModal(p, v); }}>
                                                    <SelectTrigger className="w-[160px] h-8" data-testid={`probleme-statut-${p.id}`}>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {Object.entries(STATUTS).map(([v, info]) => (
                                                            <SelectItem key={v} value={v}>
                                                                <span className={`inline-block px-2 py-0.5 rounded text-xs ${info.color}`}>{info.label}</span>
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </TableCell>
                                            <TableCell className="text-xs text-slate-500">{formatDate(p.updated_at)}</TableCell>
                                            <TableCell className="text-right">
                                                <button
                                                    onClick={() => handleDelete(p.id)}
                                                    className="p-1.5 rounded-md hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                                                    title="Supprimer"
                                                    data-testid={`probleme-delete-${p.id}`}
                                                >
                                                    <Trash2 size={15} />
                                                </button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Modal Signaler un problème */}
            <Dialog open={showCreate} onOpenChange={(o) => { setShowCreate(o); if (!o) resetForm(); }}>
                <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="probleme-form">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Plus size={18} className="text-[#2e7cd6]" />
                            Signaler un problème
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-2">
                        {/* Client search */}
                        <div className="relative">
                            <label className="text-xs text-slate-600 mb-1 block">Client *</label>
                            <div className="relative">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <Input
                                    value={clientSearch}
                                    onChange={e => { setClientSearch(e.target.value); setFormClientNom(e.target.value); }}
                                    onFocus={() => clientResults.length > 0 && setShowClientDropdown(true)}
                                    placeholder="Rechercher un client par nom..."
                                    className="pl-10"
                                    autoFocus
                                    data-testid="probleme-client-search"
                                />
                            </div>
                            {showClientDropdown && clientResults.length > 0 && (
                                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                    {clientResults.map(c => (
                                        <button
                                            key={c.id}
                                            onClick={() => selectClient(c)}
                                            className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex items-center justify-between border-b border-slate-100 last:border-0"
                                            data-testid={`probleme-client-option-${c.id}`}
                                        >
                                            <span className="font-medium text-slate-800 text-sm">{c.client_nom}</span>
                                            <div className="flex items-center gap-3 text-xs text-slate-500">
                                                {c.telephone && <span className="flex items-center gap-1"><Phone size={10} />{c.telephone}</span>}
                                                {c.email && <span className="flex items-center gap-1"><Mail size={10} />{c.email}</span>}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {formClientNom && (formClientTel || formClientEmail) && (
                                <div className="flex gap-4 mt-2 text-xs text-slate-500">
                                    {formClientTel && <span className="flex items-center gap-1"><Phone size={12} />{formClientTel}</span>}
                                    {formClientEmail && <span className="flex items-center gap-1"><Mail size={12} />{formClientEmail}</span>}
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="text-xs text-slate-600 mb-1 block">Description *</label>
                            <Textarea
                                value={formDescription}
                                onChange={e => setFormDescription(e.target.value)}
                                rows={3}
                                placeholder="Décrivez le problème..."
                                className="resize-none"
                                data-testid="probleme-description"
                            />
                        </div>

                        {/* Pièces jointes */}
                        <div>
                            <label className="text-xs text-slate-600 mb-1 flex items-center gap-1">
                                <Paperclip size={12} className="text-slate-500" />
                                Fichiers / Photos (optionnel)
                            </label>
                            <p className="text-[11px] text-slate-400 mb-2">Jusqu'à {MAX_FILES} fichiers · max 10 Mo chacun</p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                                className="hidden"
                                onChange={handleCreateFiles}
                                data-testid="probleme-create-files-input"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => fileInputRef.current?.click()}
                                className="border-dashed border-slate-300 text-slate-600 hover:bg-slate-50"
                                data-testid="probleme-create-files-pick-btn"
                            >
                                <Upload size={14} className="mr-1.5" />
                                Choisir un fichier ou une photo
                            </Button>
                            {formFiles.length > 0 && (
                                <ul className="mt-2 space-y-1.5">
                                    {formFiles.map((f, i) => (
                                        <li key={i} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-1.5 text-sm" data-testid={`probleme-create-file-${i}`}>
                                            <Paperclip size={12} className="text-slate-400 shrink-0" />
                                            <span className="flex-1 truncate text-slate-700 text-xs">{f.name}</span>
                                            <span className="text-[10px] text-slate-400 shrink-0">{formatBytes(f.size)}</span>
                                            <button type="button" onClick={() => setFormFiles(prev => prev.filter((_, idx) => idx !== i))} className="p-1 rounded hover:bg-red-50 text-red-500 shrink-0" title="Retirer">
                                                <X size={12} />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" size="sm" onClick={() => { resetForm(); setShowCreate(false); }} disabled={saving}>Annuler</Button>
                            <Button size="sm" className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white" onClick={handleCreate} disabled={saving} data-testid="probleme-confirm-btn">
                                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                                Signaler
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Modal Changement de statut (remarque obligatoire) */}
            <Dialog open={!!statutModal} onOpenChange={(o) => { if (!o) setStatutModal(null); }}>
                <DialogContent className="sm:max-w-[480px]" data-testid="probleme-statut-dialog">
                    {statutModal && (
                        <>
                            <DialogHeader>
                                <DialogTitle>Passer en « {STATUTS[statutModal.target]?.label} »</DialogTitle>
                            </DialogHeader>
                            <div className="pt-2">
                                <p className="text-xs text-slate-500 mb-2">Une remarque est obligatoire pour valider ce changement.</p>
                                <Textarea
                                    value={statutRemarque}
                                    onChange={e => setStatutRemarque(e.target.value)}
                                    rows={4}
                                    placeholder="Décrire le motif / la progression..."
                                    className="resize-none"
                                    autoFocus
                                    data-testid="probleme-statut-remarque"
                                />
                            </div>
                            <div className="flex justify-end gap-2 pt-3">
                                <Button variant="outline" size="sm" onClick={() => setStatutModal(null)} disabled={statutSaving}>Annuler</Button>
                                <Button
                                    size="sm"
                                    className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white"
                                    onClick={submitStatut}
                                    disabled={statutSaving || !statutRemarque.trim()}
                                    data-testid="probleme-statut-confirm"
                                >
                                    {statutSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                                    Valider
                                </Button>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Modal Historique */}
            <Dialog open={!!historyProbleme} onOpenChange={(o) => { if (!o) { setHistoryProbleme(null); setHistoryData([]); } }}>
                <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto" data-testid="probleme-history-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Star size={18} className="text-[#2e7cd6] fill-[#2e7cd6]" />
                            Historique du problème
                        </DialogTitle>
                    </DialogHeader>
                    {historyLoading ? (
                        <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#2e7cd6]" /></div>
                    ) : historyData.length === 0 ? (
                        <div className="text-center py-10 text-slate-400 text-sm">Aucun historique</div>
                    ) : (
                        <div className="relative pl-6 mt-2">
                            <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-slate-200" />
                            <div className="space-y-5">
                                {historyData.map((entry, idx) => (
                                    <div key={idx} className="relative flex items-start gap-3" data-testid={`probleme-history-entry-${idx}`}>
                                        <div className={`absolute -left-6 top-1 w-3 h-3 rounded-full border-2 ${idx === 0 ? 'bg-[#2e7cd6] border-[#2e7cd6]' : 'bg-white border-slate-300'}`} />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {entry.ancien_statut && (
                                                    <>
                                                        <Badge variant="outline" className={`${STATUTS[entry.ancien_statut]?.color || ''} border text-xs`}>
                                                            {STATUTS[entry.ancien_statut]?.label || entry.ancien_statut}
                                                        </Badge>
                                                        <ArrowRight size={14} className="text-slate-400 shrink-0" />
                                                    </>
                                                )}
                                                <Badge variant="outline" className={`${STATUTS[entry.nouveau_statut]?.color || ''} border text-xs`}>
                                                    {STATUTS[entry.nouveau_statut]?.label || entry.nouveau_statut}
                                                </Badge>
                                                <span className="text-slate-300 mx-1">|</span>
                                                <Clock size={12} className="text-slate-400 shrink-0" />
                                                <span className="text-xs text-slate-400">{formatDate(entry.changed_at)}</span>
                                                <span className="text-slate-300">|</span>
                                                <span className="text-xs text-slate-400">{entry.changed_by || 'Système'}</span>
                                            </div>
                                            {entry.remarque && (
                                                <div className="mt-2 bg-amber-50 border-l-2 border-amber-400 rounded-r px-3 py-2">
                                                    <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">{entry.remarque}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Modal Détail problème */}
            <Dialog open={!!detailProbleme} onOpenChange={(o) => { if (!o) { setDetailProbleme(null); cancelEditDescription(); } }}>
                <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="probleme-detail-dialog">
                    {detailProbleme && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <Eye size={18} className="text-[#2e7cd6]" />
                                    Détail du problème
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 pt-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Badge variant="outline" className={`border text-xs ${STATUTS[detailProbleme.statut]?.color || ''}`}>
                                        {STATUTS[detailProbleme.statut]?.label || detailProbleme.statut}
                                    </Badge>
                                    <Badge variant="outline" className="border text-xs bg-slate-100 text-slate-700 border-slate-200">
                                        <span className="flex items-center gap-1.5"><AlertTriangle size={11} />{detailProbleme.client_nom || '-'}</span>
                                    </Badge>
                                </div>
                                <div className="grid grid-cols-2 gap-3 text-xs text-slate-500">
                                    <div>
                                        <p className="uppercase tracking-wide font-semibold text-[10px] text-slate-400">Signalé par</p>
                                        <p className="text-slate-700 font-medium mt-0.5">{detailProbleme.created_by || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="uppercase tracking-wide font-semibold text-[10px] text-slate-400">Créé le</p>
                                        <p className="text-slate-700 font-medium mt-0.5">{formatDate(detailProbleme.created_at)}</p>
                                    </div>
                                    <div>
                                        <p className="uppercase tracking-wide font-semibold text-[10px] text-slate-400">Modifié le</p>
                                        <p className="text-slate-700 font-medium mt-0.5">{formatDate(detailProbleme.updated_at)}</p>
                                    </div>
                                    <div>
                                        <p className="uppercase tracking-wide font-semibold text-[10px] text-slate-400">Coût</p>
                                        <p className="text-slate-700 font-medium mt-0.5">{detailProbleme.cout || '-'}</p>
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-xs uppercase tracking-wide font-semibold text-slate-400">Description</p>
                                        {!editingDescription && (
                                            <button
                                                type="button"
                                                onClick={startEditDescription}
                                                className="flex items-center gap-1 text-xs text-[#2e7cd6] hover:text-[#2568b8] font-medium"
                                                data-testid="probleme-edit-description-btn"
                                            >
                                                <Pencil size={12} />
                                                Modifier
                                            </button>
                                        )}
                                    </div>
                                    {editingDescription ? (
                                        <div className="space-y-2">
                                            <Textarea
                                                value={descriptionDraft}
                                                onChange={(e) => setDescriptionDraft(e.target.value)}
                                                rows={4}
                                                className="text-sm"
                                                data-testid="probleme-edit-description-textarea"
                                                autoFocus
                                            />
                                            <div className="flex items-center justify-end gap-2">
                                                <Button type="button" variant="outline" size="sm" onClick={cancelEditDescription} disabled={savingDescription} data-testid="probleme-edit-description-cancel-btn">
                                                    Annuler
                                                </Button>
                                                <Button type="button" size="sm" className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white gap-1" onClick={saveDescription} disabled={savingDescription} data-testid="probleme-edit-description-save-btn">
                                                    {savingDescription ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                                    Enregistrer
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                                            <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                                                {detailProbleme.description || <span className="italic text-slate-400">Pas de description</span>}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Pièces jointes */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-xs uppercase tracking-wide font-semibold text-slate-400 flex items-center gap-1">
                                            <Paperclip size={12} /> Pièces jointes ({(detailProbleme.dossiers || []).length})
                                        </p>
                                        <input
                                            ref={detailFileInputRef}
                                            type="file"
                                            accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                                            className="hidden"
                                            onChange={handleAddFile}
                                            data-testid="probleme-detail-file-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => detailFileInputRef.current?.click()}
                                            disabled={uploadingFile}
                                            className="flex items-center gap-1 text-xs text-[#2e7cd6] hover:text-[#2568b8] font-medium disabled:opacity-50"
                                            data-testid="probleme-add-file-btn"
                                        >
                                            {uploadingFile ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                                            Ajouter un fichier
                                        </button>
                                    </div>
                                    {(detailProbleme.dossiers || []).length === 0 ? (
                                        <p className="text-xs italic text-slate-400">Aucun fichier</p>
                                    ) : (
                                        <ul className="space-y-1.5">
                                            {detailProbleme.dossiers.map((d, i) => (
                                                <li key={d.filename || i} className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-2 hover:border-[#2e7cd6] transition-colors" data-testid={`probleme-dossier-${detailProbleme.id}-${i}`}>
                                                    <FileText size={14} className="text-red-500 shrink-0" />
                                                    <span className="flex-1 truncate text-sm text-slate-700">{d.name || d.filename}</span>
                                                    <a
                                                        href={`${API}${d.url}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="p-1.5 rounded hover:bg-blue-50 text-[#2e7cd6] shrink-0"
                                                        title="Ouvrir / télécharger"
                                                        data-testid={`probleme-dossier-download-${detailProbleme.id}-${i}`}
                                                    >
                                                        <Download size={14} />
                                                    </a>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteFile(d.filename)}
                                                        className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 shrink-0"
                                                        title="Supprimer le fichier"
                                                        data-testid={`probleme-delete-file-${detailProbleme.id}-${i}`}
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
